/*
 * Entity card (design/50-components.md "Card"). The navigating variant: an action card without
 * the two things that promise a launch, so it must not grow a trailing arrow or a lift.
 */

/*
 * The design-system `Link` takes no `className` — its props are a closed list — so the anchor is
 * styled from this wrapper instead of being handed a class. It is an inline anchor by default and
 * the card is a block, so it is told to fill the grid track rather than shrink to its text.
 * `decoration="none"` already removes the underline; colour is set per element inside.
 */
.wrapper > a {
  display: block;
  inline-size: 100%;
  border-radius: var(--border-radius__xl);
}

/*
 * Radius, border, inset and the 20px inline-start are the action card's, to the number, so a
 * favorites grid under a prompts grid keeps one icon column down the page. The two differ in the
 * things that carry meaning — no resting shadow, no lift, no arrow — rather than in their metrics,
 * because a reader comparing two sections reads a 4px step in the icon column as a mistake and
 * reads a missing arrow as a different kind of card.
 */
.root {
  display: flex;
  align-items: center;
  gap: var(--space__md);
  padding: var(--space__lg);
  padding-inline-start: 20px;
  border: 1px solid var(--color__gray-300);
  border-radius: var(--border-radius__xl);
  background: var(--color__white);
  text-align: start;
  /* 150ms, the moving-affordance duration (design/10-foundations.md "Motion"). */
  transition:
    border-color 150ms,
    background-color 150ms;
}

/*
 * The action card's hover pair, **without its shadow step**. Keeping the fill and border makes the
 * two kinds of card feel like siblings under the pointer; dropping the 1px-to-4px lift is what
 * keeps "something starts here" meaning only that.
 */
.wrapper > a:hover .root,
.wrapper > a:focus-visible .root {
  border-color: var(--color__lilac-300);
  background: var(--color__lilac-50);
}

/* The platform ring traces the anchor, which is the card's own box (design/50-components.md
   "Focus and interaction states"), so the ring and the border describe one shape. */
.wrapper > a:focus-visible {
  outline: var(--focus-ring__width) solid var(--focus-ring__color);
  outline-offset: var(--focus-ring__offset);
}

/* 40 by 40, `lilac-100` ground, `lilac-500` glyph, `lg` radius: the action card's chip. */
.chip {
  flex: 0 0 auto;
  inline-size: 40px;
  block-size: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius__lg);
  background: var(--color__lilac-100);
  color: var(--color__lilac-500);
}

/*
 * `sm` between the kind and the name: one step above the `xs` the card rules forbid here, and one
 * below the `md` a stat tile puts under its label.
 *
 * `xs` (4px) is the icon-to-text step at the bottom of the depth ladder, and using it between two
 * lines of one text block is the defect the card rules name: it leaves less white than the lines
 * have between themselves, so the label stops belonging to the name (design/50-components.md
 * "Card", "Inside the card the steps come off the depth ladder, and `xs` is not one of them").
 *
 * `md` is the [Stat tile](#stat-tile) step, and it is too much here. That tile sets 12px under a
 * label because the figure below it is 32px, and the rule it follows is that the bigger the type,
 * the bigger the step it needs below it (design/10-foundations.md "Space and rhythm"). A 14px name
 * is not a 32px figure, so the same 12px reads as a gap between two blocks rather than as a label
 * bound to the thing it names. `sm` is also the action card's own step between its two lines, so
 * the two cards in one column share their internal text rhythm.
 *
 * Changing this step does not change the card's height. Both lines are trimmed to cap and
 * baseline, so the block measures about 27px against the chip's 40px, and the chip is what the row
 * is as tall as. Tune the step for how the pair reads, not for the card's size.
 *
 * `min-inline-size: 0` is what lets the name ellipsis-truncate inside the flex row.
 */
.text {
  min-inline-size: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
}
